import { useEffect, useState } from 'react'
import cn from 'classnames'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import {
   Card,
   Spinner,
} from '@forwardflow/features/campaign-flow/components/ui/primitives'
import {
   IActivityItem,
   TActivityKind,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { campaignFlowService } from '@forwardflow/features/campaign-flow/services'
import { timeAgo } from '@forwardflow/features/campaign-flow/utils/links'

const ACTIVITY: Record<
   TActivityKind,
   { label: string; icon: TFlowIcon; cls: string }
> = {
   appointment: {
      label: 'Appointments',
      icon: 'cal',
      cls: 'bg-[#00d857]/15 text-[#5BE39A]',
   },
   transfer: {
      label: 'Transfers',
      icon: 'phone',
      cls: 'bg-[#9061DA]/25 text-[#DAC2FF]',
   },
   reply: {
      label: 'Replies',
      icon: 'chat',
      cls: 'bg-[#5B8DEF]/20 text-[#9DBBFF]',
   },
}

/**
 * What Last 30 days: recent bookings, live transfers and replies,
 * for the whole organization or one campaign. Each opens the conversation.
 */
export const ActivityPanel = ({
   campaignId,
   campaignNames,
   open,
   className,
   fill,
}: {
   /** One campaign, or every campaign when omitted. */
   campaignId?: string
   /** Names for the campaign line on each item (the organization view). */
   campaignNames?: Record<string, string>
   open: (
      kind: TActivityKind | null,
      leadId?: string,
      campaignId?: string
   ) => void
   className?: string
   /** Fill the parent's height and scroll the list inside, instead of a capped height. */
   fill?: boolean
}) => {
   const [items, setItems] = useState<IActivityItem[] | null>(null)
   const [error, setError] = useState<string | null>(null)
   const [reload, setReload] = useState(0)
   const [only, setOnly] = useState<TActivityKind | null>(null)
   useEffect(() => {
      let live = true
      setItems(null)
      setError(null)
      void campaignFlowService
         .recentActivity(campaignId)
         .then((x) => live && setItems(x))
         .catch(() => live && setError('Could not load recent activity.'))
      return () => {
         live = false
      }
   }, [campaignId, reload])
   const all = items ?? []
   const shown = only ? all.filter((x) => x.kind === only) : all
   const count = (k: TActivityKind) => all.filter((x) => x.kind === k).length
   const nameOf = (x: IActivityItem) =>
      x.campaignName ??
      (x.campaignId ? campaignNames?.[x.campaignId] : undefined)

   return (
      <Card
         className={cn(
            'overflow-hidden flex flex-col',
            fill && 'lg:h-full min-h-0',
            className
         )}
         testId="cf-activity"
      >
         <div className="px-4 pt-3 pb-2.5 border-b border-white/[0.06] flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
               <span className="text-sm font-semibold">Recent activity</span>
               <span className="text-xs text-[#818181]">Last 30 days</span>
            </div>
            <div className="flex flex-wrap gap-1.5" role="tablist">
               {([null, 'appointment', 'transfer', 'reply'] as const).map(
                  (k) => (
                     <button
                        key={k ?? 'all'}
                        type="button"
                        role="tab"
                        aria-selected={only === k}
                        onClick={() => setOnly(k)}
                        className={cn(
                           'h-7 px-2.5 rounded-full text-xs font-semibold border',
                           only === k
                              ? 'border-[#9061DA] bg-[#9061DA]/20 text-white'
                              : 'border-white/[0.1] text-[#9ca3af] hover:text-white'
                        )}
                        data-testid={`cf-activity-${k ?? 'all'}`}
                     >
                        {k
                           ? `${ACTIVITY[k].label} ${count(k)}`
                           : `All ${all.length}`}
                     </button>
                  )
               )}
            </div>
         </div>
         <div
            className={cn(
               'flex-1 min-h-0 overflow-y-auto cf-scroll',
               !fill && 'max-h-[560px]'
            )}
         >
            {error ? (
               <div role="alert" className="px-4 py-8 text-center text-sm text-[#9ca3af]">
                  <p>{error}</p>
                  <button type="button" onClick={() => setReload((n) => n + 1)} className="mt-3 font-semibold text-[#DAC2FF] hover:text-white">Retry</button>
               </div>
            ) : !items ? (
               <div className="flex justify-center py-8">
                  <Spinner size={18} />
               </div>
            ) : shown.length ? (
               shown.map((x) => {
                  const a = ACTIVITY[x.kind]
                  const campaign = nameOf(x)
                  return (
                     <button
                        key={x.id}
                        type="button"
                        onClick={() => open(x.kind, x.leadId, x.campaignId)}
                        className="w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/[0.05] last:border-0 hover:bg-white/[0.04]"
                        data-testid={`cf-activity-item-${x.id}`}
                        title="Open the conversation"
                     >
                        <span
                           className={cn(
                              'w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5',
                              a.cls
                           )}
                        >
                           <FlowIcon name={a.icon} size={13} />
                        </span>
                        <span className="min-w-0 flex-1">
                           <span className="flex items-center gap-2">
                              <b className="text-[13px] font-semibold truncate">
                                 {x.leadName}
                              </b>
                              <span className="ml-auto text-[11px] text-[#818181] whitespace-nowrap">
                                 {x.timeLabel ? `${x.timeLabel} · ` : ''}{timeAgo(x.at)}
                              </span>
                           </span>
                           <span className="block text-xs text-[#9ca3af] line-clamp-2">
                              {x.text}
                           </span>
                           {!campaignId && campaign && (
                              <span className="block text-[11px] text-[#DAC2FF]/80 truncate mt-0.5">
                                 {campaign}
                              </span>
                           )}
                        </span>
                     </button>
                  )
               })
            ) : (
               <div className="px-4 py-8 text-center text-[13px] text-[#9ca3af]">
                  Nothing here in the last 30 days.
               </div>
            )}
         </div>
         <button
            type="button"
            onClick={() => open(only)}
            className="w-full h-11 border-t border-white/[0.06] text-[13px] font-semibold text-[#DAC2FF] hover:bg-white/[0.04] flex items-center justify-center gap-1.5 shrink-0"
            data-testid="cf-open-conversations"
         >
            Open in Conversations
            <FlowIcon name="chev" size={13} />
         </button>
      </Card>
   )
}
